import React, { useState } from 'react';
import { CheckCircleIcon, CircleIcon, ScanIcon, AlertCircleIcon, PackageIcon } from 'lucide-react';
interface Ingredient {
  id: string;
  name: string;
  amount: number;
  unit: string;
  tolerance: number;
  requiresWeighing: boolean;
  isMultiScan?: boolean;
  scannedCount?: number;
  maxScans?: number;
  paletteNumber?: string;
  scannedBatches?: string[];
  isCustomerBarcode?: boolean;
  isPackagingLabel?: boolean;
  description?: string;
}
interface Step {
  id: string;
  name: string;
  ingredients: Ingredient[];
}
interface ProductionStepProps {
  step: Step;
  completedIngredients: string[];
  ingredientBatches: {
    [key: string]: string;
  };
  onSelectIngredient: (ingredientId: string) => void;
  onComplete: (ingredientId: string, batchNumber: string) => void;
  orderId: string;
}
export const ProductionStep = ({
  step,
  completedIngredients,
  ingredientBatches,
  onSelectIngredient,
  onComplete,
  orderId
}: ProductionStepProps) => {
  if (step.ingredients.length === 0) {
    return <div className="flex flex-col h-full" data-id="element-92">
        <div className="flex-1" data-id="element-93">
          <div className="bg-gray-50 p-4 rounded-md text-center" data-id="element-94">
            <p data-id="element-95">Dieser Schritt erfordert keine Zutat-Abwiegung.</p>
            <p className="text-sm text-gray-500 mt-2" data-id="element-96">
              Führen Sie den Produktionsschritt durch und fahren Sie fort.
            </p>
          </div>
        </div>
      </div>;
  }
  const generateBatchNumber = () => {
    // Generiere eine 6-stellige Zufallszahl
    const randomNumber = Math.floor(100000 + Math.random() * 900000);
    return randomNumber.toString();
  };
  const [showWarning, setShowWarning] = useState<string | null>(null);
  const [warning, setWarning] = useState<WarningType | null>(null);
  const [multiScanState, setMultiScanState] = useState<{
    [key: string]: {
      scannedCount: number;
      paletteNumber?: string;
      scannedBatches: string[];
    };
  }>({});
  const [showPinModal, setShowPinModal] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const handleSimulateScan = () => {
    const nextIngredient = step.ingredients.find(ing => {
      if (ing.isMultiScan) {
        // Bei Schälerei: Erlaube nur einen Scan
        if (step.name === 'Schälerei') {
          return !multiScanState[ing.id]?.scannedCount;
        }
        const currentCount = multiScanState[ing.id]?.scannedCount || 0;
        return currentCount < (ing.maxScans || 0);
      }
      return !completedIngredients.includes(ing.id);
    });
    if (nextIngredient) {
      if (nextIngredient.isPackagingLabel || !nextIngredient.isMultiScan && !nextIngredient.requiresWeighing) {
        // 50% Chance auf eine Warnung
        if (Math.random() < 0.5) {
          const warningType = Math.random() < 0.5 ? 'wrong-product' : 'expired';
          setWarning({
            id: nextIngredient.id,
            type: warningType
          });
        } else {
          const batchNumber = generateBatchNumber();
          handleCompleteIngredient(nextIngredient.id, batchNumber);
        }
      } else if (nextIngredient.isMultiScan) {
        const currentState = multiScanState[nextIngredient.id] || {
          scannedCount: 0,
          scannedBatches: []
        };
        // Generiere Palettennummer beim Scan
        const newState = {
          scannedCount: currentState.scannedCount + 1,
          paletteNumber: currentState.paletteNumber || generateBatchNumber(),
          scannedBatches: [...currentState.scannedBatches, orderId]
        };
        setMultiScanState(prev => ({
          ...prev,
          [nextIngredient.id]: newState
        }));
        // Bei Schälerei: Nach dem ersten Scan direkt abschließen
        if (step.name === 'Schälerei') {
          handleCompleteIngredient(nextIngredient.id, newState.paletteNumber);
        }
      } else if (nextIngredient.requiresWeighing) {
        onSelectIngredient(nextIngredient.id);
      } else {
        // Normale Zutat-Logik für nicht-wiegende Zutaten
        if (Math.random() < 0.3) {
          const warningType = Math.random() < 0.5 ? 'wrong-product' : 'expired';
          setWarning({
            id: nextIngredient.id,
            type: warningType
          });
        } else {
          const batchNumber = generateBatchNumber();
          handleCompleteIngredient(nextIngredient.id, batchNumber);
        }
      }
    }
  };
  // Neue Hilfsfunktion für das direkte Abhaken
  const handleCompleteIngredient = (ingredientId: string, batchNumber: string) => {
    onComplete(ingredientId, batchNumber);
  };
  const handlePinSubmit = () => {
    if (pin === '1234') {
      setShowPinModal(false);
      setPin('');
      setWarning(null);
      const batchNumber = generateBatchNumber();
      handleCompleteIngredient(warning!.id, batchNumber);
    } else {
      setPinError(true);
      setPin('');
    }
  };
  const currentStepIngredients = step.ingredients.map(ing => ing.id);
  const completedStepIngredients = completedIngredients.filter(id => currentStepIngredients.includes(id));
  // Special render for Schälerei
  if (step.name === 'Schälerei') {
    const schaelereiIngredient = step.ingredients[0];
    const scannedItems = multiScanState[schaelereiIngredient.id]?.scannedBatches || [];
    const handleSimulateScan = () => {
      const availableBatches = ['RW-22445', 'RW-22446', 'RW-22447', 'RW-22448', 'RW-22449', 'RW-22450'];
      const unscannedBatches = availableBatches.filter(batch => !scannedItems.includes(batch));
      if (unscannedBatches.length > 0) {
        // Randomly select 1-3 batches to scan
        const batchesToScan = Math.min(Math.floor(Math.random() * 3) + 1, unscannedBatches.length);
        const selectedBatches = unscannedBatches.slice(0, batchesToScan);
        const currentState = multiScanState[schaelereiIngredient.id] || {
          scannedCount: 0,
          paletteNumber: generateBatchNumber(),
          scannedBatches: []
        };
        const newState = {
          scannedCount: currentState.scannedCount + selectedBatches.length,
          paletteNumber: currentState.paletteNumber,
          scannedBatches: [...currentState.scannedBatches, ...selectedBatches]
        };
        setMultiScanState(prev => ({
          ...prev,
          [schaelereiIngredient.id]: newState
        }));
        // Complete the ingredient if this is the first scan
        if (!currentState.scannedBatches.length) {
          handleCompleteIngredient(schaelereiIngredient.id, newState.paletteNumber);
        }
      }
    };
    return <div className="flex flex-col h-full" data-id="element-97">
        {/* Progress indicator at the top */}
        <div className="bg-white py-2 px-3 rounded-lg shadow-sm mb-2 sticky top-0" data-id="element-98">
          <div className="flex items-center gap-2" data-id="element-99">
            <div className="flex-1" data-id="element-100">
              <div className="flex items-baseline gap-1 mb-1" data-id="element-101">
                <span className="text-base font-bold text-[#e76405]" data-id="element-102">
                  {scannedItems.length}
                </span>
                <span className="text-xs text-gray-500" data-id="element-103">von</span>
                <span className="text-sm font-medium" data-id="element-104">
                  {schaelereiIngredient.maxScans}
                </span>
                <span className="text-xs text-gray-500 ml-1" data-id="element-105">
                  Chargen auf Palette
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-106">
                <div className="h-full bg-[#e76405] rounded-full transition-all duration-300" style={{
                width: `${scannedItems.length / (schaelereiIngredient.maxScans || 1) * 100}%`
              }} data-id="element-107" />
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1" data-id="element-108">
          <div className="space-y-1.5 mt-2" data-id="element-109">
            {['RW-22445', 'RW-22446', 'RW-22447', 'RW-22448', 'RW-22449', 'RW-22450'].map(batch => {
            const isScanned = scannedItems.includes(batch);
            return <div key={batch} className={`p-3 border rounded-lg ${isScanned ? 'border-green-500 bg-green-50' : 'border-gray-200'}`} data-id="element-110">
                  <div className="flex justify-between items-start gap-3" data-id="element-111">
                    <div className="min-w-0 flex-1" data-id="element-112">
                      <div className="text-sm font-semibold text-gray-900" data-id="element-113">
                        Rohwurst Premium Klassik
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5" data-id="element-114">
                        Charge: {batch}
                      </div>
                    </div>
                    {isScanned && <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg" data-id="element-115">
                        <div data-id="element-116">
                          <div className="text-[10px] text-gray-500" data-id="element-117">
                            Palette
                          </div>
                          <div className="text-sm font-medium text-[#e76405]" data-id="element-118">
                            {multiScanState[schaelereiIngredient.id]?.paletteNumber || '-'}
                          </div>
                        </div>
                        <div className="h-8 w-[1px] bg-gray-200" data-id="element-119"></div>
                        <div data-id="element-120">
                          <div className="text-[10px] text-gray-500" data-id="element-121">
                            Status
                          </div>
                          <div className="text-xs text-gray-700" data-id="element-122">Gescannt</div>
                        </div>
                      </div>}
                  </div>
                </div>;
          })}
          </div>
        </div>
        {/* Demo Button */}
        <div className="fixed bottom-2 right-2 opacity-30 hover:opacity-100 transition-opacity" data-id="element-123">
          <button onClick={handleSimulateScan} className="bg-gray-200 text-gray-600 py-1 px-2 rounded text-[10px] hover:bg-gray-300" data-id="element-124">
            <span className="flex items-center gap-1" data-id="element-125">
              <ScanIcon size={10} data-id="element-126" />
              Demo
            </span>
          </button>
        </div>
      </div>;
  }
  // Add this near the top of the file, after other interfaces
  interface CustomerProduct {
    id: string;
    name: string;
    quantity: number;
    unit: string;
    articleNumber: string;
    batchNumber: string;
  }
  // Update the mock customer products data
  const mockCustomerProducts: CustomerProduct[] = [{
    id: '1',
    name: 'Rohbratwurst Premium Classic',
    articleNumber: 'RB-1234',
    batchNumber: '887987997-01'
  }, {
    id: '2',
    name: 'Rohbratwurst Traditionell',
    articleNumber: 'RB-5678',
    batchNumber: '887987997-02'
  }, {
    id: '3',
    name: 'Rohbratwurst Kräuter',
    articleNumber: 'RB-9012',
    batchNumber: '887987997-03'
  }];
  if (step.name === 'Verpackung') {
    const [scannedProducts, setScannedProducts] = useState<string[]>([]);
    const [showProducts, setShowProducts] = useState<string[]>([]);
    const handleDemoScan = () => {
      // Find the next unshown product
      const nextProduct = mockCustomerProducts.find(product => !showProducts.includes(product.id));
      if (nextProduct) {
        // Show and scan the product immediately
        setShowProducts(prev => [...prev, nextProduct.id]);
        setScannedProducts(prev => [...prev, nextProduct.id]);
      }
    };
    return <div className="flex flex-col h-full" data-id="element-127">
        <div className="bg-white rounded-lg p-4 mb-4" data-id="element-128">
          <div className="text-sm font-medium text-gray-600 mb-2" data-id="element-129">
            Bestellter Artikel
          </div>
          <div className="bg-gray-50 rounded-lg p-3" data-id="element-130">
            <div className="text-lg font-bold text-[#e76405]" data-id="element-131">
              Rohbratwurst Premium Classic
            </div>
            <div className="text-sm text-gray-500" data-id="element-132">Artikelnummer: RB-1234</div>
          </div>
        </div>
        <div className="space-y-2" data-id="element-133">
          <div className="text-sm font-medium text-gray-600 mb-2" data-id="element-134">
            Kundenprodukte
          </div>
          {showProducts.length === 0 ? <div className="bg-gray-50 rounded-lg p-6 text-center" data-id="element-135">
              <div className="text-gray-500" data-id="element-136">Keine Produkte gescannt</div>
            </div> : <>
              {/* Status Banner - Show after first scan */}
              {scannedProducts.length > 0 && <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2" data-id="element-137">
                  <div className="flex items-center justify-between" data-id="element-138">
                    <div className="flex items-center gap-2" data-id="element-139">
                      <CheckCircleIcon className="text-green-500" size={18} data-id="element-140" />
                      <span className="text-sm text-green-700" data-id="element-141">
                        Mindestens ein Produkt wurde gescannt
                      </span>
                    </div>
                    <span className="text-xs text-green-600" data-id="element-142">
                      Schritt kann abgeschlossen werden
                    </span>
                  </div>
                </div>}
              {/* Scanned Products List */}
              {mockCustomerProducts.filter(product => showProducts.includes(product.id)).map(product => <div key={product.id} className={`bg-white p-3 rounded-lg shadow-sm transition-colors ${scannedProducts.includes(product.id) ? 'bg-green-50/70 border border-green-200' : ''}`} data-id="element-143">
                    <div className="flex justify-between items-start" data-id="element-144">
                      <div data-id="element-145">
                        <div className="font-medium" data-id="element-146">{product.name}</div>
                        <div className="text-sm text-gray-500" data-id="element-147">
                          Art.Nr: {product.articleNumber}
                        </div>
                        <div className="text-sm text-[#e76405] mt-1" data-id="element-148">
                          Charge: {product.batchNumber}
                        </div>
                      </div>
                      <div className="flex items-center justify-center h-6 w-6" data-id="element-149">
                        {scannedProducts.includes(product.id) && <CheckCircleIcon className="text-green-500" size={20} data-id="element-150" />}
                      </div>
                    </div>
                  </div>)}
            </>}
        </div>
        {/* Complete Step Button - Only show after first scan */}
        {scannedProducts.length > 0 && <div className="mt-4" data-id="element-151">
            <button onClick={() => {
          // Ensure step completion by calling onComplete with the first scanned product
          const firstScannedProduct = mockCustomerProducts.find(product => scannedProducts.includes(product.id));
          if (firstScannedProduct) {
            onComplete(step.ingredients[0].id, firstScannedProduct.batchNumber);
          }
        }} className="w-full bg-[#e76405] text-white py-3 px-4 rounded-lg text-base font-medium flex items-center justify-center gap-2" data-id="element-152">
              <CheckCircleIcon size={20} data-id="element-153" />
              Prozessschritt abschließen
            </button>
          </div>}
        {/* Demo Button */}
        <div className="fixed bottom-2 right-2 opacity-30 hover:opacity-100 transition-opacity" data-id="element-154">
          <button onClick={handleDemoScan} className="bg-gray-200 text-gray-600 py-1 px-2 rounded text-[10px] hover:bg-gray-300" data-id="element-155">
            <span className="flex items-center gap-1" data-id="element-156">
              <ScanIcon size={10} data-id="element-157" />
              Demo
            </span>
          </button>
        </div>
      </div>;
  }
  return <div className="flex flex-col h-full" data-id="element-158">
      {/* Progress indicator at the top */}
      <div className="bg-white py-2 px-3 rounded-lg shadow-sm mb-2 sticky top-0" data-id="element-159">
        <div className="flex items-center gap-2" data-id="element-160">
          <div className="flex-1" data-id="element-161">
            <div className="flex items-baseline gap-1 mb-1" data-id="element-162">
              <span className="text-base font-bold text-[#e76405]" data-id="element-163">
                {completedStepIngredients.length}
              </span>
              <span className="text-xs text-gray-500" data-id="element-164">von</span>
              <span className="text-sm font-medium" data-id="element-165">
                {currentStepIngredients.length}
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-166">
                {step.name === 'Schälerei' ? 'Chargen auf Palette' : 'Rohstoffe abgewogen'}
              </span>
            </div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-167">
              <div className="h-full bg-[#e76405] rounded-full transition-all duration-300" style={{
              width: `${completedStepIngredients.length / currentStepIngredients.length * 100}%`
            }} data-id="element-168" />
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1" data-id="element-169">
        <div className="space-y-1.5 mt-2" data-id="element-170">
          {step.ingredients.map(ingredient => {
          const isCompleted = completedIngredients.includes(ingredient.id);
          const currentCount = multiScanState[ingredient.id]?.scannedCount || 0;
          const remainingSlots = (ingredient.maxScans || 0) - currentCount;
          return <div key={ingredient.id} className={`p-3 border rounded-lg ${isCompleted ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-171">
                <div className="flex justify-between items-start gap-3" data-id="element-172">
                  <div className="min-w-0 flex-1" data-id="element-173">
                    <div className="text-sm font-semibold text-gray-900" data-id="element-174">
                      {ingredient.name}
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5" data-id="element-175">
                      {ingredient.isMultiScan ? !multiScanState[ingredient.id]?.scannedCount ? 'Chargenetiketten scannen' : 'Chargenetikett wurde gescannt' : ingredient.description}
                    </div>
                    {ingredient.isMultiScan && <div className="text-xs text-gray-600 mt-2" data-id="element-176">
                        <span className="font-medium" data-id="element-177">Verfügbare Chargen:</span>
                        <div className="mt-1" data-id="element-178">
                          <div className="bg-gray-50 rounded" data-id="element-179">
                            <div className="font-medium text-gray-900 p-2 border-b" data-id="element-180">
                              Rohwurst Premium Klassik
                            </div>
                            <div className="divide-y" data-id="element-181">
                              {['RW-22445', 'RW-22446', 'RW-22447', 'RW-22448', 'RW-22449', 'RW-22450'].map(batch => <div key={batch} className="flex items-center justify-between p-2" data-id="element-182">
                                  <div className="flex items-center gap-2" data-id="element-183">
                                    <div className="text-[#e76405] font-mono" data-id="element-184">
                                      {batch}
                                    </div>
                                    <div className="text-xs text-gray-500" data-id="element-185">
                                      Premium Qualität
                                    </div>
                                  </div>
                                  {multiScanState[ingredient.id]?.scannedBatches?.includes(batch) ? <CheckCircleIcon size={18} className="text-green-500" data-id="element-186" /> : <CircleIcon size={18} className="text-gray-300" data-id="element-187" />}
                                </div>)}
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>
                  {isCompleted && !ingredient.isMultiScan && <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg" data-id="element-188">
                      <div data-id="element-189">
                        <div className="text-[10px] text-gray-500" data-id="element-190">Charge</div>
                        <div className="text-sm font-medium text-[#e76405]" data-id="element-191">
                          {ingredientBatches[ingredient.id] || 'RW-22445'}
                        </div>
                      </div>
                      <div className="h-8 w-[1px] bg-gray-200" data-id="element-192"></div>
                      <div data-id="element-193">
                        <div className="text-[10px] text-gray-500" data-id="element-194">
                          {step.name === 'Schälerei' ? 'Produkt' : 'Lieferant'}
                        </div>
                        <div className="text-xs text-gray-700" data-id="element-195">
                          {step.name === 'Schälerei' ? 'Rohwurst Premium Klassik' : 'ACME GmbH'}
                        </div>
                      </div>
                      <div className="h-8 w-[1px] bg-gray-200" data-id="element-196"></div>
                      <div data-id="element-197">
                        <div className="text-[10px] text-gray-500" data-id="element-198">MHD</div>
                        <div className="text-xs text-gray-700" data-id="element-199">31.12.24</div>
                      </div>
                    </div>}
                  {ingredient.isMultiScan && <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg" data-id="element-200">
                      <div data-id="element-201">
                        <div className="text-[10px] text-gray-500" data-id="element-202">Charge</div>
                        <div className="text-sm font-medium text-[#e76405]" data-id="element-203">
                          {multiScanState[ingredient.id]?.paletteNumber || '-'}
                        </div>
                      </div>
                      <div className="h-8 w-[1px] bg-gray-200" data-id="element-204"></div>
                      <div data-id="element-205">
                        <div className="text-[10px] text-gray-500" data-id="element-206">Produkt</div>
                        <div className="text-xs text-gray-700" data-id="element-207">
                          Rohwurst Premium Klassik
                        </div>
                      </div>
                      <div className="h-8 w-[1px] bg-gray-200" data-id="element-208"></div>
                      <div data-id="element-209">
                        <div className="text-[10px] text-gray-500" data-id="element-210">Status</div>
                        <div className="text-xs text-gray-700" data-id="element-211">
                          {multiScanState[ingredient.id]?.scannedCount || 0}/
                          {ingredient.maxScans} Chargen
                        </div>
                      </div>
                    </div>}
                </div>
              </div>;
        })}
        </div>
      </div>
      {/* Warnung Modal */}
      {warning && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-212">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-xl" data-id="element-213">
            <div className="flex flex-col items-center gap-4" data-id="element-214">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center" data-id="element-215">
                <AlertCircleIcon size={40} className="text-red-500" data-id="element-216" />
              </div>
              <h3 className="text-xl font-bold text-center" data-id="element-217">ACHTUNG!</h3>
              <p className="text-center text-gray-700 font-medium" data-id="element-218">
                {warning.type === 'wrong-product' ? 'Dieser Rohstoff ist nicht für diesen Produktionsauftrag vorgesehen!' : 'Das Mindesthaltbarkeitsdatum ist überschritten!'}
              </p>
              <div className="text-sm text-center text-gray-500" data-id="element-219">
                {warning.type === 'wrong-product' ? 'Bitte überprüfen Sie den Rohstoff und scannen Sie den korrekten Artikel.' : 'Bitte informieren Sie den Schichtleiter.'}
              </div>
              <div className="w-full mt-4 space-y-2" data-id="element-220">
                <button onClick={() => setShowPinModal(true)} className="w-full py-3 px-4 bg-[#e76405] text-white rounded-lg font-medium hover:bg-[#d55a00] transition-colors" data-id="element-221">
                  Freigeben
                </button>
                <button onClick={() => setWarning(null)} className="w-full py-3 px-4 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 transition-colors" data-id="element-222">
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Demo Button */}
      <div className="fixed bottom-2 right-2 opacity-30 hover:opacity-100 transition-opacity" data-id="element-223">
        <button onClick={handleSimulateScan} className="bg-gray-200 text-gray-600 py-1 px-2 rounded text-[10px] hover:bg-gray-300" data-id="element-224">
          <span className="flex items-center gap-1" data-id="element-225">
            <ScanIcon size={10} data-id="element-226" />
            Demo
          </span>
        </button>
      </div>
      {/* PIN Modal */}
      {showPinModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-227">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-xl" data-id="element-228">
            <div className="flex flex-col items-center gap-4" data-id="element-229">
              <h3 className="text-xl font-bold" data-id="element-230">PIN-Eingabe</h3>
              <p className="text-sm text-center text-gray-500" data-id="element-231">
                Bitte geben Sie den 4-stelligen PIN-Code ein
              </p>
              <input type="password" maxLength={4} value={pin} onChange={e => {
            setPin(e.target.value.replace(/[^0-9]/g, ''));
            setPinError(false);
          }} className={`w-full text-center text-2xl tracking-widest py-3 px-4 border-2 rounded-lg ${pinError ? 'border-red-500' : 'border-gray-300'}`} autoFocus data-id="element-232" />
              {pinError && <p className="text-sm text-red-500" data-id="element-233">Falscher PIN-Code</p>}
              <div className="w-full space-y-2" data-id="element-234">
                <button onClick={handlePinSubmit} disabled={pin.length !== 4} className={`w-full py-3 px-4 rounded-lg font-medium ${pin.length === 4 ? 'bg-[#e76405] text-white hover:bg-[#d55a00]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'} transition-colors`} data-id="element-235">
                  Bestätigen
                </button>
                <button onClick={() => {
              setShowPinModal(false);
              setPin('');
              setPinError(false);
            }} className="w-full py-3 px-4 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 transition-colors" data-id="element-236">
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};
interface WarningType {
  id: string;
  type: 'wrong-product' | 'expired';
}